import {
  CaretDown,
  CaretUp,
  CheckCircle,
  CurrencyCircleDollar,
  PencilSlash,
  Sparkle,
  WarningDiamond,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { useContext, useEffect, useMemo, useState } from "react";
import UserContext from "src/context/UserContext";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import { DataTable } from "../TabelaRelatorio";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export default function Produtividade({ useUser = false }) {
  const { userData } = useContext(UserContext);
  const userNome = useUser && userData?.user?.nome ? userData.user.nome : "";
  const [dadosTabelaProdutividade, setDadosTabelaProdutividade] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [executed, setExecuted] = useState(false);
  const [tags, setTags] = useState({
    epico: [],
    hu: [],
    selectedEpico: "",
    selectedHU: "",
  });

  const params = useMemo(() => {
    if (!selectedMonth) return {};

    const [year, month] = selectedMonth.split("-");

    const params = {
      dataInicio: new Date(year, month - 1, 1).toISOString().split("T")[0],
      dataFim: new Date(year, month, 0).toISOString().split("T")[0],
    };
    return params;
  }, [selectedMonth]);

  const months = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const date = new Date(today.getFullYear(), today.getMonth() - i - 1, 1);
      const monthName = date
        .toLocaleDateString("pt-BR", { month: "long" })
        .replace(/^\w/, (c) => c.toUpperCase());

      return {
        label: `${monthName}/${date.getFullYear()}`,
        labelSpaced: `${monthName} ${date.getFullYear()}`,
        shortLabel: `${monthName}/${date.getFullYear().toString().slice(-2)}`,
        value: date.toISOString().slice(0, 7),
      };
    });
  }, []);

  const monthData = months.find((month) => month.value === selectedMonth);
  const hasData = dadosTabelaProdutividade.length > 0;

  const createBillableCell = (value, alignment = "center") => (
    <div className={`text-sm font-normal text-gray-800 text-${alignment}`}>
      <div className="gap-1 flex items-center justify-center">
        <CurrencyCircleDollar size={24} className="text-green-500" />
        {value}
      </div>
    </div>
  );

  const createNullableCell = (value, alignment = "center") => (
    <div className={`text-sm font-normal text-gray-800 text-${alignment}`}>
      {value || (
        <div className="gap-1 flex items-center justify-center">
          <WarningDiamond size={24} className="text-yellow-500" />
          Não disponível
        </div>
      )}
    </div>
  );

  const calcularStatus = (nomeEpico, epicoHoras, historias) => {
    const hasNullValue =
      historias.some(
        (hu) =>
          hu.data_inicio === null ||
          hu.data_fim === null ||
          hu.tempo_esperado === null ||
          hu.tempo_gasto === null
      ) ||
      nomeEpico === null ||
      epicoHoras === null;

    if (hasNullValue) return "Incompleto";

    const somaTempoEstimado = historias.reduce(
      (acc, historia) => acc + (historia.tempo_esperado || 0),
      0
    );

    const somaTempoGasto = historias.reduce(
      (acc, historia) => acc + (historia.tempo_gasto || 0),
      0
    );

    return epicoHoras > somaTempoEstimado && somaTempoGasto <= epicoHoras
      ? "Produtivo"
      : "Ok";
  };

  const externalColumns = useMemo(
    () => [
      {
        accessorKey: "usuario_colaborador",
        header: "Membro",
        cell: ({ row }) => {
          const { historias_de_usuario, ...rest } = row.original;

          const hasNullValue =
            Object.values(rest).some((v) => v === null) ||
            historias_de_usuario.some((hu) =>
              Object.values(hu).some((v) => v === null)
            );

          return (
            <div
              className={cn(
                "text-sm font-normal text-gray-800 gap-1 flex items-center",
                hasNullValue ? "justify-start" : "justify-center"
              )}
            >
              {hasNullValue && (
                <WarningDiamond size={24} className="text-yellow-500" />
              )}
              {row.getValue("usuario_colaborador")}
            </div>
          );
        },
      },
      {
        accessorKey: "nome_projeto",
        header: "Projeto",
        cell: (info) => createNullableCell(info.getValue()),
      },
      {
        accessorKey: "nome_epico",
        header: "Título do Épico",
        cell: ({ row }) => {
          const titulo = row.getValue("nome_epico");
          const tagsEpico = row.original.tag_epico || [];
          const hasFaturado = tagsEpico.includes("faturado");

          if (hasFaturado) {
            return createBillableCell(titulo);
          } else {
            return createNullableCell(titulo);
          }
        },
      },
      {
        accessorKey: "epico_horas",
        header: "Horas do Épico",
        cell: (info) => createNullableCell(info.getValue()),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const status = calcularStatus(
            row.original.nome_epico,
            row.original.epico_horas,
            row.original.historias_de_usuario || []
          );

          return (
            <div className="text-center">
              <Badge
                className={cn(
                  "text-sm font-medium gap-1 justify-center",
                  status === "Produtivo"
                    ? "text-emerald-700 border-emerald-500 bg-green-50 hover:bg-green-200"
                    : status === "Incompleto"
                    ? "text-[#EB8825] border-[#EB8825] bg-[#FFD5BE] hover:bg-[#FFBF9C]"
                    : "text-blue-600 border-blue-400 bg-blue-200 hover:bg-blue-300"
                )}
              >
                {status === "Produtivo" ? (
                  <Sparkle size={12} />
                ) : status === "Incompleto" ? (
                  <PencilSlash size={12} />
                ) : (
                  <CheckCircle size={12} />
                )}
                {status}
              </Badge>
            </div>
          );
        },
      },
      {
        id: "expander",
        header: () => null,
        cell: ({ row }) => {
          return (
            <Button
              variant="ghost"
              size="icon"
              onClick={row.getToggleExpandedHandler()}
            >
              {row.getIsExpanded() ? (
                <CaretUp size={17} />
              ) : (
                <CaretDown size={17} />
              )}
            </Button>
          );
        },
      },
    ],
    []
  );

  const internalColumns = useMemo(
    () => [
      {
        accessorKey: "titulo_hu",
        header: "Atividade",
        cell: ({ row }) => {
          const titulo = row.getValue("titulo_hu");
          const tagsHu = row.original.tag_hu || [];
          const hasFaturado = tagsHu.includes("faturado");

          if (hasFaturado) {
            return createBillableCell(titulo, "left");
          } else {
            return createNullableCell(titulo, "left");
          }
        },
      },
      {
        accessorKey: "data_inicio",
        header: "Data início",
        cell: (info) => createNullableCell(info.getValue()),
      },
      {
        accessorKey: "data_fim",
        header: "Data fim",
        cell: (info) => createNullableCell(info.getValue()),
      },
      {
        accessorKey: "tempo_esperado",
        header: "Horas estimadas",
        cell: (info) => createNullableCell(info.getValue()),
      },
      {
        accessorKey: "tempo_gasto",
        header: "Horas realizadas",
        cell: (info) => createNullableCell(info.getValue()),
      },
    ],
    []
  );

  const fetchProdutividade = async () => {
    if (!selectedMonth) return;

    setLoading(true);
    setDadosTabelaProdutividade([]);

    try {
      if (useUser && userData.user.id_colaborador) {
        params.idUsuario = userData.user.id_colaborador;
      }

      if (tags.selectedEpico && tags.selectedEpico !== "") {
        params.tagEpico = tags.selectedEpico;
      }

      if (tags.selectedHU && tags.selectedHU !== "") {
        params.tagHu = tags.selectedHU;
      }

      const response = await api.get("/api/relatorio/produtividade", {
        params,
      });
      setDadosTabelaProdutividade(response.data);
      ["idUsuario", "tagEpico", "tagHu"].forEach((key) => {
        delete params[key];
      });
    } catch (error) {
      console.error("Erro ao buscar dados de produtividade:", error);
    } finally {
      setLoading(false);
      setExecuted(true);
    }
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    setDadosTabelaProdutividade([]);
    setExecuted(false);
  };

  const handleTagsChange = (field, value) => {
    setTags((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="m-auto w-fit">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: "4rem" }}></i>
        </div>
      );
    }

    if (executed && !hasData) {
      return (
        <div className="flex items-center justify-center p-8 w-full gap-4">
          <p className="text-2xl font-medium text-slate-900">
            Não há dados para o mês selecionado
          </p>
        </div>
      );
    }

    if (hasData) {
      return (
        <>
          <div className="flex items-center justify-center p-8 w-full gap-4">
            <p className="text-2xl font-medium text-slate-900">
              Mês de {monthData?.label}
            </p>
          </div>
          <div className="mt-4">
            <DataTable
              data={dadosTabelaProdutividade}
              columns={externalColumns}
              month={monthData?.labelSpaced}
              userNome={userNome}
            >
              {(row) => (
                <DataTable
                  data={row.historias_de_usuario}
                  columns={internalColumns}
                />
              )}
            </DataTable>
          </div>
        </>
      );
    }

    return null;
  };

  useEffect(() => {
    const fetchTagsEpico = async () => {
      setTags((prev) => ({
        ...prev,
        epico: [],
      }));

      try {
        const response = await api.get("/epicTags", {
          params,
        });
        setTags((prev) => ({
          ...prev,
          epico: response.data,
        }));
      } catch (error) {
        console.error("Erro ao buscar tags dos épicos:", error);
      }
    };

    const fetchTagsHU = async () => {
      setTags((prev) => ({
        ...prev,
        hu: [],
      }));

      try {
        const response = await api.get("/userStoryTags", {
          params,
        });
        setTags((prev) => ({
          ...prev,
          hu: response.data,
        }));
      } catch (error) {
        console.error("Erro ao buscar tags das HUs:", error);
      }
    };

    fetchTagsEpico();
    fetchTagsHU();
  }, [params]);

  return (
    <div className={cn(useUser ? "flex flex-col items-center justify-center" : "")}>
      {useUser && (
        <>
          <nav className="navigation" style={{ marginTop: "100px" }}>
            <ul>
              <li>
                <a href="/perfil">Home</a>
              </li>
              <li>/</li>
              <li>
                <a href="/produtividade/#">Colaborador</a>
              </li>
              <li>/</li>
              <li>
                <a href="/produtividade">Produtividade</a>
              </li>
            </ul>
          </nav>
          <div
            style={{
              width: "95%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginBottom: "3rem",
            }}
          >
            <p
              className="relatorio-title"
              style={{
                fontSize: "35px",
                fontWeight: "700",
                color: "#00293B",
                fontFamily: "satoshi",
                marginTop: "5px",
                marginBottom: "5px",
              }}
            >
              Produtividade
            </p>
            <div
              style={{
                width: "100%",
                borderBottom: "2px solid #006998",
                marginBottom: "1.5rem",
              }}
            />
          </div>
        </>
      )}
      <div style={{ width: "95%", margin: "0 auto" }}>
        <div className={cn(useUser ? "w-[95%]" : "")}>
          <p className="filter-title">Selecione a competência para visualizar:</p>
          <div className="gap-3 p-3 flex">
            <Select onValueChange={handleMonthChange}>
              <SelectTrigger className="w-52">
                <SelectValue placeholder="Mês" />
              </SelectTrigger>
              <SelectContent position="item-aligned">
                <SelectGroup>
                  {months.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.shortLabel}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            {/* Tags épico */}
            <Select
              onValueChange={(value) =>
                handleTagsChange("selectedEpico", value === "clear" ? "" : value)
              }
              disabled={
                !selectedMonth ||
                tags.epico.length === 0 ||
                tags.epico[0] === "Sem tags"
              }
              value={tags.selectedEpico}
            >
              <SelectTrigger className="w-52">
                <SelectValue
                  placeholder={
                    tags.epico.length === 0 && selectedMonth
                      ? "Carregando..."
                      : tags.epico[0] === "Sem tags"
                      ? tags.epico[0]
                      : "Tags Épico"
                  }
                />
              </SelectTrigger>
              <SelectContent position="item-aligned">
                <SelectGroup>
                  <SelectItem value="clear">Nenhuma</SelectItem>
                  {tags.epico.map((tag) => (
                    <SelectItem key={tag} value={tag}>
                      {tag}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            {/* Tags HU */}
            <Select
              onValueChange={(value) =>
                handleTagsChange("selectedHU", value === "clear" ? "" : value)
              }
              disabled={
                !selectedMonth ||
                tags.hu.length === 0 ||
                tags.hu[0] === "Sem tags"
              }
              value={tags.selectedHU}
            >
              <SelectTrigger className="w-52">
                <SelectValue
                  placeholder={
                    tags.hu.length === 0 && selectedMonth
                      ? "Carregando..."
                      : tags.hu[0] === "Sem tags"
                      ? tags.hu[0]
                      : "Tags Hu"
                  }
                />
              </SelectTrigger>
              <SelectContent position="item-aligned">
                <SelectGroup>
                  <SelectItem value="clear">Nenhuma</SelectItem>
                  {tags.hu.map((tag) => (
                    <SelectItem key={tag} value={tag}>
                      {tag}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            <Button
              disabled={!selectedMonth || loading}
              onClick={fetchProdutividade}
              className={`bg-gray-800 hover:bg-gray-700 text-white ${
                loading ? "disabled:bg-zinc-400" : ""
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2" /> Carregando
                </>
              ) : (
                "Executar"
              )}
            </Button>
          </div>

          {renderContent()}
        </div>
      </div>
    </div>
  );
}
